import Heading from "@components/Booking/Heading";
import DatePicker from "@components/Booking/SlotPicking/components/DatePicker";
import {useDataForImageRow} from "@components/Booking/SlotPicking/hooks/useDataForImageRow";
import useDatePickerControls from "@components/Booking/SlotPicking/hooks/useDatePickerControls";
import {useSlotReselectionStepViewedEvent} from "@components/Booking/SlotPicking/hooks/useSlotReselectionStepViewedEvent";
import Button, {ButtonVariant} from "@components/Button";
import ImageRow from "@components/ImageRow";
import {BookingPracticeAppointmentReasonFieldsFragment} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useBookIfUniqueAndGoToConfirmation} from "@page-containers/booking/utils/useBookIfUniqueAndGoToConfirmation";
import {MonolithLocationFields} from "@services/graphql/queries/location";
import {ApptSlot, fetchSlots} from "@services/monolith/availability";
import {Trans, useTranslation} from "@i18n/client";
import React, {useCallback, useEffect, useState} from "react";
import {SpecialtyId} from "src/constants/specialtyIds";
import {useTwMediaQuery} from "src/hooks/useTwMediaQuery";
import {Breakpoint} from "src/constants/breakpoint";
import {v4} from "uuid";

import {I18nNamespace} from "@i18n";
import {getEndOfDay, getStartOfDay} from "../../../../utils/timeUtils";
import SlotGroup from "./components/SlotGroup";
import {emitChangeDateEvent, emitSlotClickedEvent, emitSlotOptionsViewedEvent} from "./events";
import {useGroupSlotsByDoctors} from "./hooks/useGroupSlotsByDoctors";
import PrimaryCareSlots from "./PrimaryCareSlots";
import {formatMinOpen} from "./utils/formatMinOpen";

type Props = {
  location: MonolithLocationFields;
  practiceAppointmentReason: BookingPracticeAppointmentReasonFieldsFragment;
  specialtyId: string;
  patientId: string;
  dropoutVisitorId?: string;
};

const SlotUnavailablePage = ({
  location,
  practiceAppointmentReason,
  specialtyId,
  patientId,
  dropoutVisitorId,
}: Props) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const i18nDB = useTranslation(I18nNamespace.DB);

  const shouldShowButton = !useTwMediaQuery(Breakpoint.SM);

  const {selectedTime, isSelectedTimeUnavailable, rescheduling, appointmentId} =
    useParsedBookingQuery();

  const [availableSlots, setAvailableSlots] = useState<ApptSlot[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [bookAppointmentLoading, setBookAppointmentLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<ApptSlot | null>(null);
  const [slotOptionsId, setSlotOptionsId] = useState<string>(v4());

  const apptReasonName = practiceAppointmentReason.appointmentReason?.name;
  const selectedLocationFirstLine = location.address.firstLine;
  const apptReasonId = practiceAppointmentReason?.appointmentReason?.id;
  const locationId = location.id;
  const timezone = location.timezone;

  const {currentCalendarTime, onPlusDay, onMinusDay} = useDatePickerControls(
    location.timezone,
    selectedTime,
  );

  const viewId = useSlotReselectionStepViewedEvent(location, specialtyId, apptReasonId);

  const onPlusDayHandler = useCallback(() => {
    emitChangeDateEvent("forward", slotOptionsId, viewId);
    onPlusDay();
  }, [slotOptionsId, viewId, onPlusDay]);

  const onMinusDayHandler = useCallback(() => {
    emitChangeDateEvent("backward", slotOptionsId, viewId);
    onMinusDay();
  }, [slotOptionsId, viewId, onMinusDay]);

  const emptyListText = t("No Slots Available!", {ns: "web-scheduling"});
  useEffect(() => {
    if (apptReasonId) {
      setIsLoading(true);
      fetchSlots({
        patientId,
        apptReasonId,
        timezone: timezone,
        locationId: locationId,
        virtual: false,
        specialtyId,
        from: Math.max(Date.now(), getStartOfDay(currentCalendarTime, timezone)),
        to: getEndOfDay(currentCalendarTime, timezone),
      }).then(slots => {
        setIsLoading(false);
        setAvailableSlots(slots.flat());
      });
    }
  }, [apptReasonId, currentCalendarTime, locationId, timezone, specialtyId, patientId]);

  const bookAppointment = useBookIfUniqueAndGoToConfirmation(undefined, dropoutVisitorId);

  const selectSlot = useCallback(
    (slot: ApptSlot) => {
      setSelectedSlot(slot);
      emitSlotClickedEvent(slot.id, slot.time, slotOptionsId, viewId);
    },
    [slotOptionsId, viewId],
  );

  const bookSlot = useCallback(
    async (slot: ApptSlot) => {
      try {
        setBookAppointmentLoading(true);
        await bookAppointment(
          {
            patientId,
            locationId: slot.locationId,
            specialtyId,
            apptReasonId: apptReasonId || "",
            slotIds: slot.slotIds,
            virtual: false,
            instant: false,
            titl: "Appointment",
            practiceId: location.practiceId,
            reschedulingApptId: appointmentId || undefined,
          },
          {
            locationSlug: location.slug,
            selectedTime: slot.time,
          },
        );
      } finally {
        setBookAppointmentLoading(false);
      }
    },
    [
      apptReasonId,
      bookAppointment,
      location.practiceId,
      location.slug,
      appointmentId,
      patientId,
      specialtyId,
    ],
  );

  const bookSelectedSlot = useCallback(() => {
    selectedSlot && bookSlot(selectedSlot);
  }, [bookSlot, selectedSlot]);

  const selectSlotAndBook = useCallback(
    (slot: ApptSlot) => {
      setSelectedSlot(slot);
      emitSlotClickedEvent(slot.id, slot.time, slotOptionsId, viewId);
      bookSlot(slot);
    },
    [bookSlot, slotOptionsId, viewId],
  );

  useEffect(() => {
    if (availableSlots) {
      const openMinutes = formatMinOpen(timezone, location.hours, currentCalendarTime);
      const newSlotOptionsId = v4();
      emitSlotOptionsViewedEvent({
        slotOptionsId: newSlotOptionsId,
        soonestSlotTime: availableSlots[0]?.time,
        numberOfSlotsInADay: availableSlots.length,
        openMinutes,
        emptyListText,
        options: availableSlots,
        viewId,
      });
      setSlotOptionsId(newSlotOptionsId);
    }
  }, [availableSlots, viewId, emptyListText, timezone, location.hours, currentCalendarTime]);

  const translatedApptReasonName = i18nDB.t(apptReasonName || "", {
    ns: "website-db",
  });

  const {name, description, src, emphasis} = useDataForImageRow(location, specialtyId);

  const slotsGroupedByDoctor = useGroupSlotsByDoctors({
    slots: availableSlots || [],
    location,
    skip: specialtyId !== SpecialtyId.PRIMARY_CARE,
  });

  const onSelectPrimaryCareSlot = useCallback(
    () => (shouldShowButton ? selectSlot : selectSlotAndBook),
    [selectSlot, selectSlotAndBook, shouldShowButton],
  );

  return (
    <div className="flex flex-col gap-8">
      {isSelectedTimeUnavailable ? (
        <div className="flex flex-col items-center gap-2 sm:gap-4">
          <Heading className="text-center max-w-md">
            {t("Sorry, this time is no longer available", {
              ns: "web-scheduling",
            })}
          </Heading>
          <p className="text-center mb-12 sm:mb-14">
            {t("Please choose a new time.", {
              ns: "web-scheduling",
            })}
          </p>
        </div>
      ) : (
        <Heading>
          <Trans ns="web-scheduling">
            Reschedule appointment <br />
            <span className="lowercase">
              <>for {{translatedApptReasonName}}</>
            </span>
          </Trans>
        </Heading>
      )}

      {rescheduling && (
        <ImageRow
          {...{
            title: name,
            description,
            emphasis,
            src,
            alt: "",
          }}
        />
      )}
      <div className="flex flex-col gap-4">
        <DatePicker
          currentCalendarTime={currentCalendarTime}
          onMinusDay={onMinusDayHandler}
          onPlusDay={onPlusDayHandler}
          selectedLocationFirstLine={selectedLocationFirstLine}
          timezone={location.timezone}
        />
        {specialtyId === SpecialtyId.PRIMARY_CARE ? (
          <PrimaryCareSlots
            slotsGroupedByProvider={slotsGroupedByDoctor.data}
            loading={slotsGroupedByDoctor.loading || isLoading}
            selectedSlotTime={selectedSlot?.time}
            getOnSlotSelection={onSelectPrimaryCareSlot}
            disabled={bookAppointmentLoading}
          />
        ) : (
          <SlotGroup
            timezone={location.timezone}
            slots={availableSlots || []}
            selectedSlotTime={selectedSlot?.time}
            onSlotSelection={shouldShowButton ? selectSlot : selectSlotAndBook}
            loading={isLoading}
            disabled={bookAppointmentLoading}
          />
        )}
      </div>
      <div className="flex sm:hidden flex-col justify-center items-center bottom-0 sticky bg-white">
        <BookItButton onClick={bookSelectedSlot} hasSelectedSlot={!!selectedSlot} />
      </div>
    </div>
  );
};

type BookItButtonProps = {
  onClick: () => void;
  hasSelectedSlot: boolean;
};
const BookItButton = ({onClick, hasSelectedSlot}: BookItButtonProps) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const {rescheduling} = useParsedBookingQuery();
  const variant = hasSelectedSlot ? ButtonVariant.FILL : ButtonVariant.GHOST;
  const text = rescheduling
    ? t("Reschedule", {ns: "web-scheduling"})
    : t("Book it", {ns: "web-scheduling"});
  return (
    <Button
      className="w-full"
      onClick={onClick}
      variant={variant}
      disabled={!hasSelectedSlot}
      data-cy="book-it-button"
    >
      {text}
    </Button>
  );
};

export default SlotUnavailablePage;
